import React from "react";
import styled from "styled-components";
import { Icon } from "../Icon";
import { LooseDynamicInput } from "../__Internal__/LooseDynamicInput";
import { LoadingState } from "../states/LoadingState";
import { style } from "../style";
import { PropNote } from "../PropX";

type LooseDynamicInputProps = React.ComponentProps<typeof LooseDynamicInput>;

type OmitTokenProps = "UNSAFE_roundedTokens" | "UNSAFE_type";

type TokenProps = Omit<LooseDynamicInputProps, OmitTokenProps>;

const DynamicValidatingMessageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${style.vars.space["2"]};
  margin-left: ${style.vars.space["1"]};
`;

export const DynamicTokenInput: React.FC<TokenProps> = (props) => {
  const note = React.useMemo(() => {
    if (props.selected.some((s) => s.state === "loading")) {
      return {
        persist: true,
        value: (
          <DynamicValidatingMessageWrapper>
            <LoadingState small />
            <span>Validating ...</span>
          </DynamicValidatingMessageWrapper>
        ),
      };
    }

    if (props.note) {
      return {
        persist: false,
        value: props.note,
      };
    }

    return {
      persist: false,
      value: (
        <>
          <Icon icon="keyboard-nav-return" small /> Press enter to add a new tag
        </>
      ),
    };
  }, [props.note, props.selected]);

  const testId = props["data-testid"] ?? "dynamic-token-input";

  return <LooseDynamicInput data-testid={testId} {...props} note={props.disabled ? null : note} />;
};

type OmitEmailProps =
  | "UNSAFE_roundedTokens"
  | "UNSAFE_filterDelay"
  | "UNSAFE_overscanCount"
  | "UNSAFE_type"
  | "leftSide"
  | "options"
  | "defaultSearchInputIcon"
  | "pattern"
  | "filterOptions"
  | "listHeader"
  | "listFooter";

type OptionalEmailProps = { options?: LooseDynamicInputProps["options"] };

type EmailProps = Omit<LooseDynamicInputProps, OmitEmailProps> & OptionalEmailProps;

export const DynamicEmailInput: React.FC<EmailProps> = (props) => {
  const testId = props["data-testid"] ?? "dynamic-email-input";
  const { selected, placeholder = "Add one or more email addresses...", options = [] } = props;

  /**
   * If an icon is not provided, we default to the email icon.
   */
  const selectedWithIcon = React.useMemo(() => {
    return selected.map((s) => ({ icon: "email", ...s }));
  }, [selected]);

  const note: PropNote = React.useMemo(() => {
    if (props.note) {
      return props.note;
    } else if (selected.some((s) => s.state === "loading")) {
      return {
        persist: true,
        value: (
          <DynamicValidatingMessageWrapper>
            <LoadingState small />
            <span>Validating emails ...</span>
          </DynamicValidatingMessageWrapper>
        ),
      };
    } else {
      return null;
    }
  }, [selected, props.note]);

  return (
    <LooseDynamicInput
      {...props}
      data-testid={testId}
      options={options}
      selected={selectedWithIcon}
      leftSide={<Icon icon="email" />}
      placeholder={placeholder}
      defaultSearchInputIcon="email"
      note={note}
      validateOn="blur"
      filterOptions={false}
      UNSAFE_filterDelay={0}
      UNSAFE_overscanCount={0}
      UNSAFE_roundedTokens
      UNSAFE_type="email"
    />
  );
};
